import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as s from './InvitePeoplePage.scss';
import { InjectedTranslateProps, translate } from 'react-i18next';
import Hint from 'wix-ui-icons-common/Hint';
import { PageSubtitle } from './PageSubtitle/PageSubtitle';
import { sendInvitesV2, showEnforceContributorsUpgradeModalIfNecessary } from '../../redux/actions/serverApi.actions';
import { useDispatch, useSelector } from 'react-redux';
import { hideToastAction, showToast } from '../../redux/actions/activeToast.actions';
import { ToastName } from '../../types/ToastNames';
import { RouteComponentProps, useHistory, useLocation, withRouter } from 'react-router-dom';
import ContributorsEmailsInput from '../../common/ContributorsEmailsInput/ContributorsEmailsInput';
import RolesListComponent, { RolesListFooter } from '../RolesListComponent/RolesListComponent';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import PageActionBar from '../PageActionBar/PageActionBar';
import { getIsSite, getLocale } from '../../redux/selectors/config';
import { InjectedExperimentsProps, withExperiments } from '@wix/wix-experiments-react';
import { inviteEmailsError, reportInvitePeopleLoad, reportSendInviteClick } from '../../redux/actions/bi.actions';
import { showModal } from '../../redux/actions/activeModal.actions';
import { ModalName } from '../../types/ModalNames';
import { useQuery } from '../../services/locationService';
import { Box, Card, FormField, Page, Text } from 'wix-style-react';
import { EnforceContributorsInviteText } from '../EnforceContributors/EnforceContributors';
import {
  getIsEnforceContributorsEnabled,
  getNumberOfAvailableContributors,
  getShouldShowUpgradeToast
} from '../../redux/selectors/teamDetails';

export interface InvitePeoplePageProps extends InjectedTranslateProps, RouteComponentProps, InjectedExperimentsProps {}

const InvitePeoplePageComponent: React.FC<InvitePeoplePageProps> = ({ t, match, experiments }) => {

  const dispatch = useDispatch<(prop: any) => Promise<any>>();
  const history = useHistory();
  const location = useLocation<any>();

  const locale = useSelector(getLocale);

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [isPageDirty, setIsPageDirty] = useState<boolean>(false);
  const [rolesError, setRolesError] = useState<boolean>(false);
  const [emailsError, setEmailsError] = useState<string>('');
  const [validEmails, setValidEmails] = useState<boolean>(false);
  const emailInputRef = useRef(null);
  const errorMessageRef = useRef(null);

  const numberOfAvailableContributors = useSelector(getNumberOfAvailableContributors);
  const isEnforceContributorsEnabled = useSelector(getIsEnforceContributorsEnabled);
  const shouldShowUpgradeToast = useSelector(getShouldShowUpgradeToast);

  const onEmailsAdded = useCallback((newEmails: string[], currentEmailsState: boolean) => {
      setValidEmails(currentEmailsState);
      setEmails(emails.concat(newEmails));
    }, [emails]);

  const onEmailRemoved = useCallback((emailToRemove: string, currentEmailsState: boolean) => {
      setValidEmails(currentEmailsState);
      setEmails(emails.filter(email => email !== emailToRemove));
    }, [emails]);

  useEffect(() => {
    dispatch(showEnforceContributorsUpgradeModalIfNecessary('/invite'));
    if (shouldShowUpgradeToast) {
      dispatch(showToast(ToastName.UpgradePlan));
      return () => dispatch(hideToastAction());
    }
  }, [dispatch, numberOfAvailableContributors, isEnforceContributorsEnabled, shouldShowUpgradeToast]);

  useEffect(() => {
    const origin = location.state?.origin || useQuery(location)?.get('referralInfo');
    dispatch(reportInvitePeopleLoad(origin));
    const selectedRoleIds = location.state?.roleIds;
    if (selectedRoleIds) {
      setSelectedRoles(selectedRoleIds);
    }
  }, [location]);

  useEffect(() => {
    if (selectedRoles.length > 0) {
      setRolesError(false);
    }
    if (emails.length > 0) {
      if (isEnforceContributorsEnabled && (emails.length > numberOfAvailableContributors)) {
        setEmailsError('upgrade_enforceContributors.emailInputErrorMessage');
      } else {
        setEmailsError('');
      }
    }
  }, [rolesError, emailsError, selectedRoles, emails, numberOfAvailableContributors, isEnforceContributorsEnabled]);

  const inviteContributors = useCallback(async () => {
    if (selectedRoles.length === 0 || emails.length === 0 || validEmails || emailsError) {
      setRolesError(selectedRoles.length === 0);
      if (emails.length === 0 || validEmails) {
        setEmailsError('emailsInput.error.empty');
        emailInputRef?.current?.scrollIntoView({ behavior: 'smooth' });
      } else if (emailsError) {
        emailInputRef?.current?.scrollIntoView({ behavior: 'smooth' });
        dispatch(inviteEmailsError(t(emailsError)));
      } else {
        errorMessageRef?.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      try {
        const origin = location.state?.origin;
        const selectedRoleIds = selectedRoles.map(selectedRole => selectedRole);
        dispatch(reportSendInviteClick(selectedRoleIds, origin, emails.length));
        await dispatch(sendInvitesV2(emails, selectedRoles));
        onInviteContributorsSuccess(selectedRoles[0], emails.length);
      } catch (error) {
        //Error message should be displayed
      }
    }
  }, [dispatch, selectedRoles, emails, emailInputRef, errorMessageRef, validEmails, emailsError]);

  const onInviteContributorsSuccess = useCallback((role, numOfEmails) => {
    const successToast = numOfEmails === 1 ? ToastName.SendInviteSuccess : ToastName.SendInvitesSuccess;
    dispatch(showToast(successToast));
    leavePage();
  }, [dispatch]);

  const leavePage = useCallback(() => history.push('/', { origin: '/invites' }), [history]);

  useEffect(() => {
    setIsPageDirty(!!selectedRoles.length || !!emails.length);
  }, [selectedRoles , emails]);

  const isSite = useSelector(getIsSite);

  const breadcrumbText = useMemo(() => t(`breadcrumbs.invite.${isSite ? 'site' : 'account'}`), [isSite]);

  const headerText = useMemo(() => t(isSite ? 'breadcrumbs.invite.site' : 'invite.invitePeopleToTeam'), [isSite]);

  const actionBar = useMemo(() => (
      <PageActionBar
        isEditable={true}
        isPageDirty={isPageDirty}
        onOkTextKey="invite.sendInvites.buttonTitle"
        onOk={inviteContributors}
        origin="/invites"/>
  ), [isPageDirty, inviteContributors]);

  const experimentCustomRoles = experiments.enabled('specs.wos.CustomRoles');

  const onBackAction = useCallback(() => {
    if (isPageDirty) {
      dispatch(showModal(ModalName.DiscardChanges, { onOk: () => history.push('/') }));
    } else {
      history.push('/');
    }
  }, [history, isPageDirty]);

  return (
    <Page height="calc(100vh - 48px)">
      <Page.Header
        showBackButton
        onBackClicked={onBackAction}
        dataHook="account-team-header"
        title={headerText}
        subtitle={<PageSubtitle locale={locale} isSite={isSite}/>}
        actionsBar={actionBar}
        breadcrumbs={<Breadcrumbs routePath={match.path} value={breadcrumbText} disableChangePath={isPageDirty}/>}
      />
      <Page.Content>
        <Card className={s.cardContent}>
          <span ref={emailInputRef}/>
          <Card.Header
            title={<div className={s.emailsInputContainer}>
                    <Box align="space-between" verticalAlign="middle">
                      <FormField required label={t('invite.email')}/>
                      <EnforceContributorsInviteText t={t} numberOfEmails={emails.length}/>
                    </Box>
                      <ContributorsEmailsInput onEmailsAdded={onEmailsAdded} onEmailRemoved={onEmailRemoved} externalErrorMessage={emailsError} emails={location.state?.emails ?? []}/>
                  </div>}
            subtitle={<Box verticalAlign="middle" paddingTop={2} paddingBottom="4px">
              <Hint style={{ fill: '#7A92A5' }}/>
              <Text className={s.hintText} size="small" secondary light>{t('invite.choosePeople.subDescription')}</Text>
            </Box>}
          />
          <Card.Divider />
          <Card.Content>
            <span ref={errorMessageRef}/>
            <RolesListComponent selectedRoles={selectedRoles} updateSelectedRoles={setSelectedRoles} shouldDisplayError={rolesError} origin="/invite" roleIds={selectedRoles} emails={emails}/>
          </Card.Content>
          {experimentCustomRoles && <RolesListFooter t={t} origin="/invite" roleIds={selectedRoles} emails={emails}/>}
        </Card>
        <Box paddingTop="large" align="right">
          {actionBar}
        </Box>
      </Page.Content>
    </Page>
  );
};

export const InvitePeoplePage = withRouter(withExperiments(translate()(InvitePeoplePageComponent)));
